import { useState, useRef, useEffect } from 'react';
import { searchPatients } from '@services/api';
import { parseApiError } from '@utils/errorUtils';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;

/**
 * Searchable patient lookup for the outgoing phone controls.
 * Type a phone number or email; results show name, account number, and company.
 * Selecting a result calls onSelect(patient) with the full patient object.
 *
 * Modeled on the CompanySelector combobox (ARIA + keyboard nav), but fetches
 * results remotely with debounce instead of filtering an in-memory list.
 */
export default function PatientSearch({ onSelect, disabled }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);

  // Close the menu when clicking outside the combobox.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Debounced remote search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setTruncated(false);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeqRef.current;
      try {
        const data = await searchPatients(trimmed);
        // Ignore out-of-order responses from earlier keystrokes.
        if (seq !== requestSeqRef.current) return;
        setResults(data.patients || []);
        setTruncated(Boolean(data.truncated));
        setHighlight(0);
        setOpen(true);
        setError('');
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setResults([]);
        setTruncated(false);
        setError(parseApiError(err) || 'Search failed');
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const choose = (patient) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(patient);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && results[highlight]) {
        e.preventDefault();
        choose(results[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const trimmedLen = query.trim().length;
  const showMenu = open && trimmedLen >= MIN_QUERY_LENGTH;

  return (
    <div className="call-input-group patient-search">
      <label htmlFor="patientSearch">Find Patient</label>
      <div className="patient-search-combo" ref={wrapRef}>
        <input
          id="patientSearch"
          type="text"
          className="patient-search-input"
          placeholder="Search by phone number or email"
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (trimmedLen >= MIN_QUERY_LENGTH) setOpen(true); }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showMenu}
          aria-controls="patient-search-listbox"
          autoComplete="off"
        />
        {showMenu && (
          <ul className="patient-search-menu" id="patient-search-listbox" role="listbox">
            {loading && <li className="patient-search-status">Searching…</li>}
            {!loading && error && <li className="patient-search-status error">{error}</li>}
            {!loading && !error && results.length === 0 && (
              <li className="patient-search-status">No patients match “{query.trim()}”.</li>
            )}
            {!loading && !error && results.map((p, i) => (
              <li
                key={p.guid}
                role="option"
                aria-selected={i === highlight}
                className={
                  'patient-search-option'
                  + (i === highlight ? ' patient-search-option-active' : '')
                  + (p.active ? '' : ' patient-search-option-inactive')
                }
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className="patient-search-option-name">
                  {p.name || '(no name)'}
                  {!p.active && <span className="patient-search-inactive-tag"> (inactive)</span>}
                </span>
                <span className="patient-search-option-meta">
                  {[p.accountNumber ? `#${p.accountNumber}` : null, p.companyName]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {p.phoneNumber && (
                  <span className="patient-search-option-phone">{p.phoneNumber}</span>
                )}
              </li>
            ))}
            {!loading && !error && truncated && (
              <li className="patient-search-more">
                Showing first {results.length} — keep typing to narrow.
              </li>
            )}
          </ul>
        )}
      </div>
      <small className="input-hint">Type at least {MIN_QUERY_LENGTH} characters.</small>
    </div>
  );
}
